import { ObservableInputsManager } from '@/shared/utils/angular-sdk';
import { ChangeDetectionStrategy, Component, forwardRef, Input, type OnChanges, type OnDestroy, type OnInit, type SimpleChanges, ViewChild } from '@angular/core';
import { type ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { AngularEditorComponent, type AngularEditorConfig, AngularEditorModule } from '@kolkov/angular-editor';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import DOMPurify from 'dompurify';
import { BehaviorSubject } from 'rxjs';
import type { RgbColorHex } from '../../theming';

@UntilDestroy()
@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./shared-ui-wysiwyg.component.ng-cli.scss'],
    selector: 'shared-ui-wysiwyg',
    templateUrl: 'shared-ui-wysiwyg.component.html',
    exportAs: 'sharedUiWysiwyg',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SharedUiWysiwygComponent),
            multi: true
        }
    ],
    imports: [AngularEditorModule]
})
export class SharedUiWysiwygComponent implements OnChanges, ControlValueAccessor, OnInit, OnDestroy {
    @Input()
    placeholder: string = '';

    @Input() disabled: boolean = false;

    @Input() bgColor: RgbColorHex | null = null;

    @Input()
    config: AngularEditorConfig = {
        editable: true,
        spellcheck: true,
        height: 'auto',
        minHeight: '300px',
        maxHeight: 'auto',
        width: 'auto',
        minWidth: '0',
        translate: 'yes',
        enableToolbar: true,
        showToolbar: true,
        defaultParagraphSeparator: '',
        defaultFontName: 'Arial',
        defaultFontSize: '3',
        fonts: [],
        toolbarHiddenButtons: [['fontName']],
        sanitize: false,
        toolbarPosition: 'top'
    };

    private _observableInputsManager = new ObservableInputsManager<SharedUiWysiwygComponent>(this);
    @ViewChild(AngularEditorComponent, { static: true })
    editor: AngularEditorComponent | null = null;

    onChange$: BehaviorSubject<(newValue: string) => void> = new BehaviorSubject((_: string) => {});
    private onTouch$: BehaviorSubject<() => void> = new BehaviorSubject(() => {});

    ngOnInit(): void {
        this.onChange$.pipe(untilDestroyed(this)).subscribe(fn => {
            if (this.editor !== null) this.editor.registerOnChange(fn);
        });
        this.onTouch$.pipe(untilDestroyed(this)).subscribe(fn => {
            if (this.editor !== null) this.editor.registerOnTouched(fn);
        });
    }
    writeValue(htmlContent: string): void {
        if (this.editor) this.editor.writeValue(htmlContent);
        else console.error('editor is not yet created');
    }
    registerOnChange(fn: (newValue: string) => void): void {
        this.onChange$.next(value => {
            const safeHtml = DOMPurify.sanitize(value, { USE_PROFILES: { html: true }, ADD_ATTR: ['target'] });
            fn(safeHtml);
        });
    }
    registerOnTouched(fn: any): void {
        this.onTouch$.next(fn);
    }

    ngOnChanges(changes: SimpleChanges): void {
        this._observableInputsManager.triggerChanges(changes);
    }
    ngOnDestroy(): void {}
}
